import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { pageKey } from '@/lib/adPages';
import {
  Home, User, Youtube, Globe, Bell, History, Library, Package, Brain, FileDown,
  HelpCircle, ListChecks, BookOpen, Star, Trophy, Sparkles, Newspaper, GraduationCap,
  FileText, Calculator, PlayCircle, Heart, Settings, ShoppingCart, Award, Target, Coins, Briefcase,
  type LucideIcon,
} from 'lucide-react';

export const UI_CONFIG_KEY = 'ui_config';

export const NAV_ICONS: Record<string, LucideIcon> = {
  Home, User, Youtube, Globe, Bell, History, Library, Package, Brain, FileDown,
  HelpCircle, ListChecks, BookOpen, Star, Trophy, Sparkles, Newspaper, GraduationCap,
  FileText, Calculator, PlayCircle, Heart, Settings, ShoppingCart, Award, Target, Coins, Briefcase,
};

export interface NavItemDef { key: string; title: string; url: string; icon: string; external?: boolean }

export const SIDEBAR_ITEMS: NavItemDef[] = [
  { key: 'side:/', title: 'Dashboard', url: '/', icon: 'Home' },
  { key: 'side:/ai-coach', title: 'ISC Coach', url: '/ai-coach', icon: 'Brain' },
  { key: 'side:/books', title: 'Books', url: '/books', icon: 'Library' },
  { key: 'side:/my-orders', title: 'My Orders', url: '/my-orders', icon: 'Package' },
  { key: 'side:/profile', title: 'Profile', url: '/profile', icon: 'User' },
  { key: 'side:/rewards', title: 'ISC Rewards', url: '/rewards', icon: 'Coins' },
  { key: 'side:/results-history', title: 'Results History', url: '/results-history', icon: 'History' },
  { key: 'side:/downloads', title: 'Download Files', url: '/downloads', icon: 'FileDown' },
  { key: 'side:/youtube', title: 'YouTube Channel', url: '/youtube', icon: 'Youtube' },
  { key: 'side:/settings/notifications', title: 'Notifications', url: '/settings/notifications', icon: 'Bell' },
  { key: 'side:blog', title: 'Blog', url: 'https://indurestudycircle.blogspot.com', icon: 'Globe', external: true },
];

export const BOTTOM_ITEMS: NavItemDef[] = [
  { key: 'bottom:/', title: 'Dashboard', url: '/', icon: 'Home' },
  { key: 'bottom:/ai-coach', title: 'ISC Coach', url: '/ai-coach', icon: 'Brain' },
  { key: 'bottom:/jobs', title: 'Job Alerts', url: '/jobs', icon: 'Briefcase' },
  { key: 'bottom:/public-quiz', title: 'Public Quiz', url: '/public-quiz', icon: 'HelpCircle' },
  { key: 'bottom:/mistakes', title: 'Mistakes', url: '/mistakes', icon: 'ListChecks' },
];

export const DASHBOARD_CARDS: { key: string; label: string }[] = [
  { key: 'examWorkspace', label: 'Choose your exam' },
  { key: 'welcome', label: 'Welcome / greeting box' },
  { key: 'live', label: 'Live Now tests' },
  { key: 'upcoming', label: 'Upcoming test countdown' },
  { key: 'currentAffairs', label: 'Current Affairs box' },
  { key: 'toppers', label: 'Top Performers strip' },
  { key: 'myBatch', label: 'MY BATCH tile' },
  { key: 'coach', label: 'ISC Coach tile' },
  { key: 'books', label: 'Books Store tile' },
  { key: 'rewards', label: 'ISC Rewards tile' },
];

/** Pages that can never be hidden. */
export const LOCKED_PAGES = ['/', '/admin', '/auth', '/exam'];

export interface UiConfig {
  pages: Record<string, boolean>; // true = hidden
  nav: Record<string, { hidden?: boolean; title?: string; icon?: string }>;
  cards: Record<string, boolean>; // true = hidden
}

export const EMPTY_UI: UiConfig = { pages: {}, nav: {}, cards: {} };

export function parseUi(raw: unknown): UiConfig {
  try {
    const v = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!v || typeof v !== 'object') return EMPTY_UI;
    const o = v as Partial<UiConfig>;
    return { pages: o.pages ?? {}, nav: o.nav ?? {}, cards: o.cards ?? {} };
  } catch { return EMPTY_UI; }
}

export function isPageHidden(cfg: UiConfig, pathname: string): boolean {
  const key = pageKey(pathname);
  if (LOCKED_PAGES.includes(key)) return false;
  return !!cfg.pages[key];
}

/** Applies admin overrides; drops hidden items and items pointing to hidden pages. */
export function resolveNav(cfg: UiConfig, items: NavItemDef[]) {
  return items
    .map(it => {
      const o = cfg.nav[it.key] ?? {};
      return {
        ...it,
        title: o.title?.trim() || it.title,
        Icon: NAV_ICONS[o.icon || it.icon] ?? NAV_ICONS[it.icon] ?? Home,
        hidden: !!o.hidden || (!it.external && isPageHidden(cfg, it.url)),
      };
    })
    .filter(it => !it.hidden);
}

let cache: UiConfig = EMPTY_UI;
let at = 0;
let pending: Promise<UiConfig> | null = null;
const subs = new Set<(c: UiConfig) => void>();

export function loadUiConfig(force = false): Promise<UiConfig> {
  if (!force && pending) return pending;
  if (!force && at && Date.now() - at < 60_000) return Promise.resolve(cache);
  pending = Promise.resolve(
    supabase.from('app_settings').select('value').eq('key', UI_CONFIG_KEY).maybeSingle()
  ).then(({ data }) => {
    cache = parseUi(data?.value); at = Date.now();
    subs.forEach(f => f(cache));
    return cache;
  }).catch(() => cache).finally(() => { pending = null; });
  return pending;
}

export function setUiConfigLocal(c: UiConfig) { cache = c; at = Date.now(); subs.forEach(f => f(c)); }

export function useUiConfig(): UiConfig {
  const [c, setC] = useState(cache);
  useEffect(() => {
    subs.add(setC);
    loadUiConfig().then(setC);
    const t = setInterval(() => loadUiConfig(), 60_000);
    return () => { subs.delete(setC); clearInterval(t); };
  }, []);
  return c;
}
